import { FC, useRef } from "react";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiTypography,
} from "../../../../../../../../components";
import { MuiDoubleLabelWithChip } from "../../../../../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import { getValueOrBlank } from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { getSmsNotification } from "../../../../../../../createProgram/components/createProgramTabs/tabs/participant/NextInfoQuestion.util";
import { ParticipantDetail } from "../../../../../../../createProgram/modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../../createProgram/siteCore/SiteCoreKeys";
import { getSelectedEmail } from "../../../../../../../participants/helper";
import { ContactAndNotificationWrapper } from "./ContactAndNotification.style";

interface ContactAndNotificationProp {
  participantDetail?: ParticipantDetail;
}

export const ContactAndNotification: FC<ContactAndNotificationProp> = ({
  participantDetail,
}) => {
  const candidateStatusRef = useRef<HTMLDivElement>(null);
  const { fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const { commonARIALabel } = useNavigationSiteCore();
  const getLabel = (key: string) => {
    return fetchLabel(contactAndNotificationPreferences, key);
  };

  const {
    contactAndNotificationPreferences,
    nextNotificationPreferencesSection,
    additionalParticipantContactDetailsSection,
    emailSubSection,
    emailIDType1,
    primaryEmailID,
    emailIDType2,
    alternateEmailID,
    phoneSubSection,
    contactType1,
    primaryPhoneNumber,
    contactType2,
    alternatePhoneNumber,
    appendedPhoneNumber,
    emailIDTypeList,
    phoneCotactTypeList,
    selectedPreferences,
    textSMSPreferenceOption,
    emailPreferenceOption,
    textSMSNotifications,
    emailNotifications,
    notOpted,
  } = programSiteCoreKeys;

  const getNotificationPreferenceOptions = (
    isSMS: boolean,
    isEmail: boolean,
    textSMSPreferenceOption: string,
    emailPreferenceOption: string,
  ) => {
    const result: { label: string; value: string }[] = [];

    if (isSMS) {
      result.push({
        label: getLabel(textSMSPreferenceOption),
        value: getLabel(textSMSPreferenceOption),
      });
    }

    if (isEmail) {
      result.push({
        label: getLabel(emailPreferenceOption),
        value: getLabel(emailPreferenceOption),
      });
    }

    return result;
  };

  const preferenceOptions = getNotificationPreferenceOptions(
    participantDetail?.nextNotificationPreferences?.isSMSNotificationEnabled ??
      false,
    participantDetail?.nextNotificationPreferences
      ?.isEmailNotificationEnabled ?? false,
    textSMSPreferenceOption,
    emailPreferenceOption,
  );

  const isAnyEnabled = (isSMS?: boolean, isEmail?: boolean): boolean => {
    return !!(isSMS || isEmail);
  };

  const isAnyNotificationEnabled = isAnyEnabled(
    participantDetail?.nextNotificationPreferences?.isSMSNotificationEnabled,
    participantDetail?.nextNotificationPreferences?.isEmailNotificationEnabled,
  );

  const renderCreateFlow = () => (
    <>
      <MuiBox className="content">
        <MuiTypography
          component="h2"
          className="sub-label-style"
          variant="h2Medium"
        >
          {getLabel(emailSubSection)}
        </MuiTypography>
        <MuiGrid container data-testid="participate-details">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(emailIDType1)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={fetchValueByKeyOfDropdown(
                contactAndNotificationPreferences,
                emailIDTypeList,
                participantDetail?.participantsContactDetails?.emailType1
                  ?.emailType ?? "",
              )}
            />
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(primaryEmailID)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={
                participantDetail?.participantsContactDetails?.emailType1?.email
              }
            />
          </MuiGrid>
        </MuiGrid>
      </MuiBox>

      <MuiBox className="content">
        <MuiGrid container data-testid="participate-details">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(emailIDType2)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={fetchValueByKeyOfDropdown(
                contactAndNotificationPreferences,
                emailIDTypeList,
                participantDetail?.participantsContactDetails?.emailType2
                  ?.emailType ?? "",
              )}
            />
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(alternateEmailID)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={
                participantDetail?.participantsContactDetails?.emailType2?.email
              }
            />
          </MuiGrid>
        </MuiGrid>
      </MuiBox>

      <MuiBox className="content">
        <MuiTypography
          component="h2"
          className="sub-label-style"
          variant="h2Medium"
        >
          {getLabel(phoneSubSection)}
        </MuiTypography>
        <MuiGrid container data-testid="participate-details">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(contactType1)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={fetchValueByKeyOfDropdown(
                contactAndNotificationPreferences,
                phoneCotactTypeList,
                participantDetail?.participantsContactDetails?.contactType1
                  ?.contactType ?? "",
              )}
            />
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(primaryPhoneNumber)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={`${getValueOrBlank(participantDetail?.participantsContactDetails?.contactType1?.countryCode)} ${getValueOrBlank(participantDetail?.participantsContactDetails?.contactType1?.number)}`.trim()}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiBox>

      <MuiBox className="content">
        <MuiGrid container data-testid="participate-details">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(contactType2)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={fetchValueByKeyOfDropdown(
                contactAndNotificationPreferences,
                phoneCotactTypeList,
                participantDetail?.participantsContactDetails?.contactType2
                  ?.contactType ?? "",
              )}
            />
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(alternatePhoneNumber)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={`${getValueOrBlank(participantDetail?.participantsContactDetails?.contactType2?.countryCode)} ${getValueOrBlank(participantDetail?.participantsContactDetails?.contactType2?.number)}`.trim()}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
      <MuiBox className="select-preference-container">
        <MuiBox className="content">
          <MuiGrid container data-testid="participate-details">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getLabel(appendedPhoneNumber)}
                replaceEmptyWithDash
                isValueWithEllipsis
                value={`${getValueOrBlank(participantDetail?.participantsContactDetails?.appendNumber?.countryCode)} ${getValueOrBlank(participantDetail?.participantsContactDetails?.appendNumber?.number)}`.trim()}
              />
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
      </MuiBox>
    </>
  );
  const renderNextFlow = () => (
    <>
      <MuiBox className="select-preference-authorized">
        <MuiTypography
          id="id-group-label"
          component={"h2"}
          className="career-goal-label-style"
          variant={"h2Bold"}
        >
          {getLabel(nextNotificationPreferencesSection)}
        </MuiTypography>
      </MuiBox>
      <MuiBox className="divider-container" />
      <MuiBox className="content">
        <MuiGrid container data-testid="participate-details">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            {isAnyNotificationEnabled ? (
              <MuiDoubleLabelWithChip
                label={getLabel(selectedPreferences)}
                replaceEmptyWithDash
                value={preferenceOptions}
                chipPopoverHeaderLabel={getLabel(selectedPreferences)}
                chipPopoverGridRef={candidateStatusRef}
                showMoreARIALabel={commonARIALabel.showNMore}
                removeChipNameARIALabel={commonARIALabel.removeChipName}
                closePopoverARIALabel={commonARIALabel.closePopover}
              />
            ) : (
              <MuiDoubleLabel
                label={getLabel(selectedPreferences)}
                replaceEmptyWithDash
                isValueWithEllipsis
                value={getLabel(notOpted)}
              />
            )}
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(textSMSNotifications)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={getSmsNotification(
                participantDetail?.nextNotificationPreferences
                  ?.isSMSNotificationEnabled ?? false,
                participantDetail?.nextNotificationPreferences
                  ?.isEmailNotificationEnabled ?? false,
                participantDetail?.nextNotificationPreferences
                  ?.smsNotification?.[0]?.number ?? "",
              )}
            />
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(emailNotifications)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={getSelectedEmail(
                participantDetail?.nextNotificationPreferences
                  ?.emailNotification,
              )}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
      <MuiBox className="select-preference-authorized">
        <MuiTypography
          id="id-group-label"
          component={"h2"}
          className="career-goal-label-style"
          variant={"h2Bold"}
        >
          {getLabel(additionalParticipantContactDetailsSection)}
        </MuiTypography>
      </MuiBox>
      <MuiBox className="divider-container" />
    </>
  );
  return (
    <ContactAndNotificationWrapper>
      {participantDetail?.isRegisteredInNext && renderNextFlow()}
      {renderCreateFlow()}
    </ContactAndNotificationWrapper>
  );
};
